import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ADMIN_PORTAL } from "@/config";

const AuthContext = createContext(null);
const IDLE_LIMIT_MS = 30 * 60 * 1000; // 30 minutes

export const ADMIN_ROLES = ["owner", "administrator", "cofounder", "finance", "technical", "developer", "reviewer", "support"];

export function AuthProvider({ children }) {
  // null = checking, false = logged out, object = logged in
  const [user, setUser] = useState(null);

  useEffect(() => {
    api.get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => setUser(false));
  }, []);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch (e) { /* ignore */ }
    setUser(false);
  }, []);

  // auto sign-out after inactivity on every portal
  const idleTimer = useRef(null);
  const resetIdleTimer = useCallback(() => {
    clearTimeout(idleTimer.current);
    idleTimer.current = setTimeout(async () => {
      try { await api.post("/auth/logout"); } catch { /* session may already be gone */ }
      setUser(false);
      toast.info("You were signed out after 30 minutes of inactivity.");
      const here = window.location.pathname;
      if (here.startsWith("/creator")) window.location.href = "/creator/login";
      else if (here.startsWith(ADMIN_PORTAL) || here.startsWith("/admin")) window.location.href = `${ADMIN_PORTAL}/login`;
    }, IDLE_LIMIT_MS);
  }, []);

  useEffect(() => {
    if (!user) return undefined;
    const events = ["pointermove", "pointerdown", "keydown", "scroll"];
    events.forEach((e) => window.addEventListener(e, resetIdleTimer, { passive: true }));
    resetIdleTimer();
    return () => {
      clearTimeout(idleTimer.current);
      events.forEach((e) => window.removeEventListener(e, resetIdleTimer));
    };
  }, [user, resetIdleTimer]);

  const value = {
    user,
    setUser,
    logout,
    loading: user === null,
    isAdmin: !!(user && ADMIN_ROLES.includes(user.role)),
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
